import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { ICompany } from 'src/app/models/company.model';
import { IModule } from 'src/app/models/module.model';
import { ITemplate } from 'src/app/models/template.model';
import { DocumentUtilsService } from 'src/app/services/document-utils.service';

export type CompanyFormMode = 'create' | 'edit';

export interface CompanyFormInfo {
  image?: File | string | null;
  imagePosition?: string;
  cnpj?: string;
  corporateName?: string;
  status?: string;
  contractedAnamneseType?: string;
  useDefaultAnamnese?: boolean;
  funcionalidadesBeta?: {
    planoTratamento?: boolean;
    calculadora?: boolean;
  };
  pacienteModelo?: string;
  // Campos de Testemunha
  firstWitnessName?: string;
  firstWitnessDocument?: string;
  secondWitnessName?: string;
  secondWitnessDocument?: string;
  // Grupo pertencente
  groupId?: string;
  // Autentique token
  hasAutentiqueToken?: boolean;
}

export interface CompanyFormAddress {
  cep?: string;
  street?: string;
  number?: string;
  complement?: string;
  neighborhood?: string;
  city?: string;
  state?: string;
}

export interface CompanyFormContact {
  phoneCountry?: string;
  phone?: string;
  email?: string;
  legalEmail?: string;
  ownerName?: string;
  ownerEmail?: string;
  password?: string;
}

export interface CompanyFormSettings {
  // Campos do step de configurações
}

@Injectable({
  providedIn: 'root'
})
export class FormCompanyService {

  private mode: CompanyFormMode = 'create';
  private companyId: string | null = null;
  private ownerId: string | null = null;
  
  // Armazena os módulos selecionados
  private selectedModules: IModule[] = [];
  
  // Armazena os templates originais da empresa (carregados da API no modo edit)
  private companyTemplates: ITemplate[] = [];
  
  // Armazena os templates selecionados
  private selectedTemplates: ITemplate[] = [];

  // Flag de token Autentique configurado
  hasAutentiqueToken = false;

  // Subject para disparar o salvamento a partir do header
  private saveRequest$ = new Subject<void>();

  private data: {
    info: CompanyFormInfo | null;
    address: CompanyFormAddress | null;
    contact: CompanyFormContact | null;
    settings: CompanyFormSettings | null;
  } = {
    info: null,
    address: null,
    contact: null,
    settings: null
  };

  private dataLoaded$ = new BehaviorSubject<boolean>(false);
  // Rotas/steps permitidas (ex.: ['info','contact','settings'])
  private allowedRoutes: string[] = [];

  constructor(private documentUtils: DocumentUtilsService) {}

  /* MODE */
  setMode(mode: CompanyFormMode): void {
    this.mode = mode;
  }

  getMode(): CompanyFormMode {
    return this.mode;
  }

  isEditMode(): boolean {
    return this.mode === 'edit';
  }

  /* COMPANY ID */
  setCompanyId(id: string): void {
    this.companyId = id;
  }

  getCompanyId(): string | null {
    return this.companyId;
  }

  /* SET COMPANY DATA FROM API (usado no modo edit) */
  setCompanyData(company: ICompany): void {
    // Armazena os módulos originais para usar no save
    this.selectedModules = company.modules || [];
    
    // Armazena os templates originais da empresa
    this.companyTemplates = company.documents || [];
    
    // Converte o logo_position da API para o formato do formulário
    let imagePosition = 'header';
    if (company.logo_position === 'WATERMARK') {
      imagePosition = 'watermark';
    } else if (company.logo_position === 'HEADERS') {
      imagePosition = 'header';
    }

    // Formata o CNPJ com máscara
    const formattedCnpj = this.documentUtils.setCpfCnpjMask(company.cnpj);

    this.data.info = {
      image: company.logo || '',
      imagePosition: imagePosition,
      cnpj: formattedCnpj,
      corporateName: company.corporate_name,
      status: company.is_blocked ? 'false' : 'true',
      contractedAnamneseType: company.contracted_anamnese_type,
      useDefaultAnamnese: company.use_default_anamnese ?? true,
      funcionalidadesBeta: {
        planoTratamento: company.has_treatment_plans ?? false,
        calculadora: company.has_calculator ?? false
      },
      pacienteModelo: company.is_model_patient_active ? 'ativo' : 'desligado',
      // Campos de Testemunha
      firstWitnessName: company.witness_one_name ?? '',
      firstWitnessDocument: company.witness_one_document ?? '',
      secondWitnessName: company.witness_two_name ?? '',
      secondWitnessDocument: company.witness_two_document ?? '',
      // Grupo pertencente
      groupId: company.group_id ?? ''
    };

    // Atualiza flag de token Autentique (não vai para CompanyFormInfo pois é readonly)
    this.hasAutentiqueToken = company.has_autentique_token ?? false;

    this.data.address = {
      cep: company.address?.zip_code,
      street: company.address?.street,
      number: company.address?.number,
      complement: company.address?.complement,
      neighborhood: company.address?.neighborhood,
      city: company.address?.city,
      state: company.address?.state
    };

    this.data.contact = {
      email: company.legal_email,
      legalEmail: company.legal_email
    };

    this.data.settings = {};

    this.dataLoaded$.next(true);
  }

  isDataLoaded(): Observable<boolean> {
    return this.dataLoaded$.asObservable();
  }

  /* STEP 1 — INFO */
  setInfo(value: CompanyFormInfo): void {
    this.data.info = value;
  }

  getInfo(): CompanyFormInfo | null {
    return this.data.info;
  }

  /* STEP 2 — ADDRESS */
  setAddress(value: CompanyFormAddress): void {
    this.data.address = value;
  }

  getAddress(): CompanyFormAddress | null {
    return this.data.address;
  }

  /* OWNER ID */
  setOwnerId(id: string): void {
    this.ownerId = id;
  }

  getOwnerId(): string | null {
    return this.ownerId;
  }

  /* STEP 3 — CONTACT */
  setContact(value: CompanyFormContact): void {
    this.data.contact = value;
  }

  getContact(): CompanyFormContact | null {
    return this.data.contact;
  }

  /* STEP 4 — SETTINGS */
  setSettings(value: CompanyFormSettings): void {
    this.data.settings = value;
  }

  getSettings(): CompanyFormSettings | null {
    return this.data.settings;
  }

  /* MODULES */
  getModules(): IModule[] {
    return this.selectedModules;
  }

  setModules(modules: IModule[]): void {
    this.selectedModules = modules;
  }

  /* TEMPLATES */
  getTemplates(): ITemplate[] {
    return this.selectedTemplates;
  }

  setTemplates(templates: ITemplate[]): void {
    this.selectedTemplates = templates;
  }

  /* COMPANY TEMPLATES (original da API - usado no modo edit) */
  getCompanyTemplates(): ITemplate[] {
    return this.companyTemplates;
  }

  /* FINAL PAYLOAD */
  getAll() {
    return {
      id: this.companyId,
      ...this.data
    };
  }

  /* RESET */
  clear(): void {
    this.mode = 'create';
    this.companyId = null;
    this.ownerId = null;
    this.selectedModules = [];
    this.companyTemplates = [];
    this.selectedTemplates = [];
    this.data = {
      info: null,
      address: null,
      contact: null,
      settings: null
    };
    this.dataLoaded$.next(false);
    this.allowedRoutes = [];
  }

  /* ALLOWED ROUTES FOR STEPS */
  setAllowedRoutes(routes: string[]): void {
    this.allowedRoutes = routes || [];
  }

  getAllowedRoutes(): string[] {
    return this.allowedRoutes;
  }

  /* SAVE REQUEST - para o header disparar o salvamento */
  triggerSave(): void {
    this.saveRequest$.next();
  }

  onSaveRequest(): Observable<void> {
    return this.saveRequest$.asObservable();
  }
}
